import { useMutation } from "@apollo/client";
import { ChangeEventHandler, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";

import createOrUpdateTeamsNotificationMutation from "../graphql/create-or-update-teams-notification";
import deleteTeamsNotificationMutation from "../graphql/delete-teams-notification";

const TeamsNotification = ({
  organization,
  teamsNotification,
  channels,
  existingChannelIds,
}: {
  organization: any;
  teamsNotification: any;
  channels: any[];
  existingChannelIds: string[];
}) => {
  const label = useLabel();
  const noSelectedChannel = {
    label: "Select Teams channel",
    value: null,
    placeholder: true,
  };
  const notificationChannelValue = teamsNotification
    ? `${teamsNotification.teamId}/${teamsNotification.channelId}`
    : null;
  const matchingChannel =
    channels.find(({ value }) => value === notificationChannelValue) ||
    (teamsNotification
      ? {
          label: `${teamsNotification.teamName} / ${teamsNotification.channelName}`,
          value: notificationChannelValue,
          teamId: teamsNotification.teamId,
          channelId: teamsNotification.channelId,
        }
      : noSelectedChannel);
  const [selectedChannel, setSelectedChannel] = useState(matchingChannel);
  const [recognitions, setRecognitions] = useState(
    teamsNotification ? teamsNotification.recognitions : true,
  );
  const [goalCheckins, setGoalCheckins] = useState(
    teamsNotification ? teamsNotification.goalCheckins : true,
  );
  const [saveNotification, { loading: loadingSave }] = useMutation(
    createOrUpdateTeamsNotificationMutation,
    {
      onError: (error) => errorNotificationVar({ title: error.message }),
    },
  );
  const [deleteNotification, { loading: loadingDelete }] = useMutation(
    deleteTeamsNotificationMutation,
    {
      onError: (error) => errorNotificationVar({ title: error.message }),
    },
  );

  const defaultChannels = [matchingChannel];
  const filteredChannels = defaultChannels
    .concat(channels)
    .filter(
      ({ value }, index, options) =>
        (!existingChannelIds.includes(value) ||
          value === matchingChannel.value) &&
        options.findIndex((option) => option.value === value) === index,
    );

  const handleDelete = () => {
    if (!teamsNotification) {
      return;
    }
    deleteNotification({ variables: { notificationId: teamsNotification.id } });
  };

  const handleSave = () => {
    if (!selectedChannel.value) {
      return;
    }
    saveNotification({
      variables: {
        organizationId: organization.id,
        notificationId: teamsNotification?.id || null,
        teamId: selectedChannel.teamId,
        channelId: selectedChannel.channelId,
        recognitions,
        goalCheckins,
      },
      onCompleted() {
        if (!teamsNotification) {
          setSelectedChannel(noSelectedChannel);
        }
      },
    });
  };

  const handleRecognitions: ChangeEventHandler<HTMLInputElement> = (event) =>
    setRecognitions(event.target.checked);
  const handleGoalCheckins: ChangeEventHandler<HTMLInputElement> = (event) =>
    setGoalCheckins(event.target.checked);
  const hasChanges =
    !teamsNotification ||
    matchingChannel.value !== selectedChannel.value ||
    recognitions !== teamsNotification.recognitions ||
    goalCheckins !== teamsNotification.goalCheckins;
  const loading = loadingSave || loadingDelete;

  return (
    <div className="py-4">
      <div className="w-full flex gap-3 items-center pb-2">
        <ComboboxGeneric
          containerClassName="max-w-96 flex-1"
          options={filteredChannels}
          loading={loading}
          disabled={loading}
          onChangeValue={setSelectedChannel}
          value={selectedChannel}
        />
        {hasChanges && (
          <Button
            text={
              teamsNotification ? "Update" : "Create new Teams notification"
            }
            disabled={!selectedChannel.value || loading}
            theme={buttonTheme.primary}
            onClick={handleSave}
          />
        )}
        {teamsNotification && (
          <Dropdown options={[{ label: "Delete", onClick: handleDelete }]} />
        )}
        {loading && <Loading mini size="5" />}
      </div>
      <div>
        <label className="pr-4">
          <input
            aria-label="Send recognitions to Teams"
            type="checkbox"
            checked={recognitions}
            onChange={handleRecognitions}
          />
          <span className="pl-1 text-sm">
            {label("recognition", { pluralize: true, capitalize: true })}
          </span>
        </label>
        <label>
          <input
            aria-label="Send goal check-ins to Teams"
            type="checkbox"
            checked={goalCheckins}
            onChange={handleGoalCheckins}
          />
          <span className="pl-1 text-sm">
            {label("goal", { capitalize: true })} Checkins
          </span>
        </label>
      </div>
    </div>
  );
};

export default TeamsNotification;
